import React from "react";
import { ClassNameProps, Row, StyleProps } from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import { Statistic, statisticColors } from "../../../../Components/Statistics";
import { Tile } from "../../../../Components/Tile";
import { defaultPostStatistics, Post, PostStatistics, usePostStatisticsQuery } from "../../../../Utilities";
import * as SocialCommentComponents from "../../SocialComment";
import * as SocialReactionComponents from "../../SocialReaction";
import * as UserViewRecordComponents from "../../UserViewRecord";

export type ControlledIndividualStatisticsTileProps = Omit<IndividualStatisticsTileProps, "isLoading" | "statistics">;

export function ControlledIndividualStatisticsTile({ postID, ...props }: ControlledIndividualStatisticsTileProps) {
	const { loading, data } = usePostStatisticsQuery({
		variables: {
			filters: { idEQ: postID },
		},
		fetchPolicy: "cache-and-network",
	});

	return <IndividualStatisticsTile postID={postID} isLoading={loading} statistics={data?.postStatistics} {...props} />;
}

export type IndividualStatisticsTileProps = {
	postID: Post["id"];
	isLoading?: boolean;
	statistics: PostStatistics | null | undefined;
} & Partial<ClassNameProps & StyleProps>;

export function IndividualStatisticsTile({
	postID,
	statistics: statisticsProp,
	className,
	style,
}: IndividualStatisticsTileProps) {
	const statistics = statisticsProp ?? defaultPostStatistics;

	const userViewRecordFilters = React.useMemo(() => ({ targetIDEQ: postID }), [postID]);
	const socialReactionFilters = React.useMemo(
		() => ({ or: [{ postIDEQ: postID }, { mediaAttachment: [{ postIDEQ: postID }] }] }),
		[postID],
	);
	const socialCommentFilters = React.useMemo(
		() => ({ or: [{ postIDEQ: postID }, { mediaAttachment: [{ postIDEQ: postID }] }] }),
		[postID],
	);

	return (
		<Tile className={className} style={style}>
			<Tile.Body>
				<Row justify="spaced-start">
					<SocialReactionComponents.UsersListModalController
						filters={socialReactionFilters}
						disabled={statistics.reactions === 0}
					>
						<Statistic
							label="Likes"
							value={statistics.reactions}
							color={statisticColors.reactionsColor}
							IconComponent={Icon.ThumbsUp}
						/>
					</SocialReactionComponents.UsersListModalController>

					<UserViewRecordComponents.UsersListModalController
						filters={userViewRecordFilters}
						disabled={statistics.views === 0}
					>
						<Statistic
							label="Views"
							value={statistics.views}
							color={statisticColors.viewsColor}
							IconComponent={Icon.Eye}
						/>
					</UserViewRecordComponents.UsersListModalController>

					<SocialCommentComponents.SocialCommentsListModalController
						filters={socialCommentFilters}
						disabled={statistics.comments === 0}
					>
						<Statistic
							label="Comments"
							value={statistics.comments}
							color={statisticColors.commentsColor}
							IconComponent={Icon.ChatCircle}
						/>
					</SocialCommentComponents.SocialCommentsListModalController>
				</Row>
			</Tile.Body>
		</Tile>
	);
}
